/**
 * Portal Manta · RodoviaContexto
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S, fmtNum, sumBy, fmtBRL } from '../../globals.js';
import { M } from '../../tokens/colors.js';

function RodoviaContexto({rodovia, dataRod, dataFin, lote}){
  const ext = rodovia.ext || 0;
  const filterRod = (cat)=>(dataRod[cat]||[]).filter(x=>x.rod===rodovia.rod);
  const sumCat = (cat,field='km')=>filterRod(cat).reduce((s,x)=>s+(x[field]||0),0);
  const cntCat = (cat,field='n')=>filterRod(cat).reduce((s,x)=>s+(x[field]||1),0);
  const dupKm = sumCat('duplicacoes','km');
  const dupTr = filterRod('duplicacoes').reduce((s,x)=>s+(x.trechos||1),0);
  const ctrKm = sumCat('contornos','km');
  const fxKm = sumCat('faixas','km');
  const fxN = cntCat('faixas');
  const margKm = sumCat('marginais','km');
  const margN = cntCat('marginais');
  const cicKm = sumCat('ciclovias','km');
  const cicN = cntCat('ciclovias');
  const oaeN = cntCat('oaes');
  const passN = cntCat('passarelas');
  const diaN = cntCat('diamantes');
  const trbN = cntCat('trombetas');
  const parN = cntCat('parclos');
  const rotN = cntCat('rotatorias');
  const interN = diaN+trbN+parN+rotN;
  const fr = ext / (lote ? parseFloat((lote.extensao||'').replace(/[^\d,.]/g,'').replace(',','.'))||1 : 1);
  const totSicro = Object.values(dataFin||{}).reduce((s,arr)=>s+sumBy(arr,'v'),0);
  const rR = totSicro * fr;
  // Tags qualitativas
  const dupPct = ext?dupKm/ext*100:0;
  const oaeDens = ext?oaeN/ext:0;
  const passDens = ext?passN/ext:0;
  const interDens = ext?interN/ext:0;
  const tags = [];
  if(dupPct>=30) tags.push({l:'INTERVENÇÃO PESADA',cor:V.or});
  else if(dupPct>=10) tags.push({l:'INTERVENÇÃO MODERADA',cor:V.am||'#d97706'});
  else if(dupPct>0) tags.push({l:'INTERVENÇÃO LEVE',cor:V.gn});
  if(oaeDens>=0.5) tags.push({l:'TRAVESSIAS FREQUENTES',cor:V.or});
  if(passDens>=0.05) tags.push({l:'CONTEXTO URBANO/PERIURBANO',cor:V.bl});
  if(cicKm>0) tags.push({l:'ÁREA CONURBADA',cor:V.bl});
  if(margKm>0) tags.push({l:'TRECHOS PERIURBANOS',cor:V.bl});
  if(interN>10) tags.push({l:'CONURBAÇÃO',cor:V.am||'#d97706'});
  if(ext>200) tags.push({l:'GRANDE EXTENSÃO',cor:V.bl});
  const intensTotal = dupPct + fxKm/ext*100*5 + oaeDens*100 + interDens*50 + (cicKm>0?10:0) + (margKm>0?5:0);
  const lvl = intensTotal>40?'COMPLEXIDADE ALTA':(intensTotal>20?'COMPLEXIDADE MÉDIA':'COMPLEXIDADE BAIXA');
  const lvlCor = intensTotal>40?V.or:(intensTotal>20?V.am||'#d97706':V.gn);
  // Catálogo de segmentos com caracterização técnica
  const segmentos = [
    {tipo:'Duplicação',qtd:dupKm,trechos:dupTr,unid:'km',cor:'#1d4ed8',
     present:dupKm>0,
     caract:'Alargamento da plataforma para pista dupla. Implica abertura de nova caixa, terraplenagem complementar, novo pavimento e novo sistema de drenagem espelhado.',
     drivers:'Terraplenagem (corte/aterro), sub-base + base estabilizada, CBUQ, drenagem dupla, OAEs gemelares, sinalização horizontal/vertical, acostamento adicional, segregação por canteiro central.'},
    {tipo:'Contornos',qtd:ctrKm,trechos:filterRod('contornos').length,unid:'km',cor:'#7c3aed',
     present:ctrKm>0,
     caract:'Trecho novo desviando o centro urbano. Plataforma greenfield com perfil rural completo.',
     drivers:'Desapropriação faixa de domínio, terraplenagem completa, pavimento de implantação, OAEs novas se houver travessias, drenagem profunda, sinalização e iluminação completas.'},
    {tipo:'Faixa Adicional',qtd:fxKm,trechos:fxN,unid:'km',cor:'#ea580c',
     present:fxKm>0,
     caract:'Terceira faixa (sobe/desce) em rampas ou seção crítica. Alargamento unilateral mantendo eixo da pista existente.',
     drivers:'Terraplenagem unilateral, pavimento de mesma estrutura, recapeamento de transição, sinalização horizontal de mudança de faixa.'},
    {tipo:'Marginais',qtd:margKm,trechos:margN,unid:'km',cor:'#16a34a',
     present:margKm>0,
     caract:'Vias paralelas em áreas urbanas/periurbanas para tráfego local segregado. Pavimento secundário, geralmente menor exigência estrutural.',
     drivers:'Drenagem urbana intensificada (sarjetas, bocas de lobo, galerias), pavimento BBTC ou similar, calçadas, iluminação pública, semaforização, paisagismo.'},
    {tipo:'Ciclovias',qtd:cicKm,trechos:cicN,unid:'km',cor:'#db2777',
     present:cicKm>0,
     caract:'Vias segregadas para bicicletas, paralelas à rodovia ou em vias laterais. Indicador de adensamento urbano.',
     drivers:'Pavimento asfáltico delgado ou concreto, segregação física (gradil/canteiro), sinalização específica, paisagismo, iluminação dedicada.'},
    {tipo:'OAE (pontes/viadutos)',qtd:oaeN,unid:'un',cor:'#7c3aed',
     present:oaeN>0,
     caract:'Obras de arte especiais sobre cursos d\'água, vales ou desníveis. Estruturas em concreto armado/protendido, vão único ou multivãos.',
     drivers:'Fundações profundas (estacas escavadas/cravadas), encontros, pilares, longarinas pré-moldadas ou moldadas in loco, tabuleiro com laje + sistema de drenagem, juntas de dilatação, dispositivos de transição, guarda-corpos.'},
    {tipo:'Passarelas',qtd:passN,unid:'un',cor:'#0891b2',
     present:passN>0,
     caract:'Travessias elevadas para pedestres em áreas urbanas/periurbanas. Indicador forte de centro urbano ou alta densidade populacional.',
     drivers:'Estrutura pré-fabricada (longarinas + tabuleiro), rampas/escadas adaptadas (acessibilidade), tela de proteção lateral, iluminação dedicada, fundações em estacas curtas ou sapatas.'},
    {tipo:'Interseções em Diamante',qtd:diaN,unid:'un',cor:'#f59e0b',
     present:diaN>0,
     caract:'Interseções desniveladas com viaduto + 4 ramos de retorno em rampas. Padrão para cruzamentos com rodovias secundárias.',
     drivers:'1 OAE (viaduto), 4 alças com terraplenagem específica, pavimento das alças, sinalização vertical, iluminação reforçada, dispositivos de canalização (defensas, refúgios).'},
    {tipo:'Interseções em Trombeta',qtd:trbN,unid:'un',cor:'#84cc16',
     present:trbN>0,
     caract:'Interseções desniveladas em "Y" ou "T" com 1 alça de retorno em loop. Tipologia para entroncamentos terminais.',
     drivers:'1 OAE (viaduto), alças com curvas amplas (raio mínimo conforme classe), iluminação completa, pavimento estruturado.'},
    {tipo:'Interseções em Parclo',qtd:parN,unid:'un',cor:'#10b981',
     present:parN>0,
     caract:'Interseção parcialmente em diamante com 2 alças desniveladas e 2 a nível. Compromisso entre custo e capacidade.',
     drivers:'OAE menor, alças combinadas (algumas em rampa, outras a nível), sinalização semafórica em ramos a nível.'},
    {tipo:'Rotatórias',qtd:rotN,unid:'un',cor:'#94a3b8',
     present:rotN>0,
     caract:'Anéis viários para distribuição de tráfego em interseções a nível. Tipologia de baixo custo para volumes médios.',
     drivers:'Anel pavimentado, ilha central paisagística, sinalização circular, iluminação central, dispositivos refletivos.'},
  ].filter(s=>s.present);

  return(<div style={{...S.cardP,marginBottom:14}}>
    {/* Header com lvl */}
    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,paddingBottom:8,borderBottom:'1px solid '+V.b1}}>
      <span style={{fontSize:11.5,fontWeight:700,color:V.br}}>📋 Descrição qualitativa · {rodovia.rod}</span>
      <span style={{...S.pillBase,background:lvlCor,color:'#fff'}}>{lvl}</span>
      <span style={{fontSize:10,color:V.gr,marginLeft:'auto'}}>R$ rateado: <b style={{color:V.br}}>{fmtBRL(rR,0)}</b> · {fmtBRL(ext?rR/ext:0,0)}/km</span>
    </div>
    {/* Tags */}
    {tags.length>0 && <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
      {tags.map((t,i)=>(<div key={i} style={{...S.pillBase,background:t.cor+'22',color:t.cor,border:'1px solid '+t.cor+'66',padding:'3px 9px',fontSize:9.5}}>{t.l}</div>))}
    </div>}
    {/* Síntese */}
    <div style={{fontSize:11,color:V.br,lineHeight:1.6,marginBottom:14}}>
      Rodovia <b>{rodovia.rod}</b> com <b>{fmtNum(ext,1)} km</b> ({(fr*100).toFixed(1)}% do lote {lote?.sig||''}). 
      Total {segmentos.length} tipos de segmentos identificados. 
      Custo SICRO rateado: <b>{fmtBRL(rR,0)}</b> ({fmtBRL(ext?rR/ext:0,0)}/km).
    </div>
    {/* Caracterização por segmento */}
    <div style={{fontSize:10.5,fontWeight:700,color:V.gr,marginBottom:8,letterSpacing:.4}}>CARACTERIZAÇÃO POR SEGMENTO</div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))',gap:10}}>
      {segmentos.map((s,i)=>(<div key={i} style={{padding:'10px 12px',background:V.g0,borderLeft:'4px solid '+s.cor,borderRadius:6}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
          <span style={{fontSize:11,fontWeight:700,color:s.cor}}>{s.tipo}</span>
          <span style={{fontSize:11,fontWeight:700,color:V.br}}>{s.unid==='km'?fmtNum(s.qtd,1)+' km':s.qtd+' '+s.unid}{s.trechos?` · ${s.trechos} trecho(s)`:''}</span>
        </div>
        <div style={{fontSize:10,color:V.br,lineHeight:1.5,marginBottom:5}}><b>Caracterização:</b> {s.caract}</div>
        <div style={{fontSize:10,color:V.gr,lineHeight:1.5}}><b style={{color:V.br}}>Drivers de custo:</b> {s.drivers}</div>
      </div>))}
    </div>
  </div>);
}

export { RodoviaContexto };
export default RodoviaContexto;
